import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../components/Form';

/** 邀請頁頂部小圖示：可改為自訂 logo URL（env: VITE_INVITE_LOGO_URL）或僅改下方常數文字 */
const INVITE_LOGO_URL = import.meta.env.VITE_INVITE_LOGO_URL as
  | string
  | undefined;
const INVITE_LOGO_LABEL = 'Emvyax';

/**
 * 邀請接受頁面
 * 採用「先預覽，後登入」的 UX 設計
 *
 * 流程：
 * 1. 公開頁面：無論是否登入，都能看到輸入框和邀請詳情
 * 2. 預覽階段：調用 GET /api/invites/info 取得邀請資訊（不需要登入）
 * 3. 確認階段：
 *    - 已登入：顯示「確認加入」按鈕，直接調用 POST /api/invites/accept
 *    - 未登入：顯示「登入並接受」按鈕，保存邀請資訊到 sessionStorage，導向 OAuth
 * 4. OAuth callback 回來後：自動完成接受流程
 */

interface InviteInfo {
  id: string;
  tenant_id: string;
  tenant_name: string;
  email: string;
  role: string;
  invite_code: string;
  status: string;
  expires_at: string | null;
}

type PageState =
  | 'idle'
  | 'loading'
  | 'confirm'
  | 'accepting'
  | 'success'
  | 'error';

export function InviteAccept() {
  const [state, setState] = useState<PageState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const urlCode = urlParams.get('code');
    const tenantsParam = urlParams.get('tenants');

    // 檢查是否有待處理的邀請（在 sessionStorage 中）
    const pendingToken = sessionStorage.getItem('pending_invite_token');
    const pendingCode = sessionStorage.getItem('pending_invite_code');
    const userToken = localStorage.getItem('tenant_token');

    // 判斷 URL 中的 token 是 JWT token 還是邀請 token
    // JWT token 格式：xxx.yyy.zzz（包含兩個點）
    const isJWTToken = urlToken && urlToken.split('.').length === 3;

    // 情況 1: OAuth callback 回來（有 JWT token 且未登入）
    if (isJWTToken && !userToken) {
      console.log('🔍 InviteAccept: 偵測到 OAuth callback 返回的 JWT token');

      localStorage.setItem('tenant_token', urlToken);
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_id');
      localStorage.removeItem('staff_name');

      if (tenantsParam) {
        try {
          const tenants = JSON.parse(tenantsParam);
          if (tenants.length === 1) {
            localStorage.setItem('tenant_id', tenants[0].tenant_id);
          }
        } catch (e) {
          console.warn('無法解析 tenants 參數', e);
        }
      }

      window.dispatchEvent(new Event('localStorageChange'));

      // 清除 URL 參數
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('token');
      newUrl.searchParams.delete('is_new');
      newUrl.searchParams.delete('tenants');
      window.history.replaceState({}, '', newUrl.toString());

      // 檢查是否有待處理的邀請（OAuth 登入完成後自動接受）
      const updatedPendingToken = sessionStorage.getItem(
        'pending_invite_token'
      );
      const updatedPendingCode = sessionStorage.getItem('pending_invite_code');
      const updatedUserToken = localStorage.getItem('tenant_token');

      if ((updatedPendingToken || updatedPendingCode) && updatedUserToken) {
        console.log('🔍 InviteAccept: OAuth 登入完成，自動完成接受流程', {
          pendingToken: !!updatedPendingToken,
          pendingCode: !!updatedPendingCode,
        });

        sessionStorage.removeItem('pending_invite_token');
        sessionStorage.removeItem('pending_invite_code');

        // 自動接受邀請
        handleAcceptInvite(updatedPendingToken, updatedPendingCode);
        return; // 重要：避免繼續執行後面的邏輯
      }
    }

    // 情況 2: 有待處理的邀請且已登入（OAuth callback 後，但上面的邏輯可能已經處理了）
    if ((pendingToken || pendingCode) && userToken) {
      console.log('🔍 InviteAccept: 有待處理的邀請且已登入，自動完成接受流程');

      sessionStorage.removeItem('pending_invite_token');
      sessionStorage.removeItem('pending_invite_code');

      // 自動接受邀請
      handleAcceptInvite(pendingToken, pendingCode);
      return; // 重要：避免繼續執行後面的邏輯
    }

    // 情況 3: 正常流程 - URL 中有邀請 token 或 code（不是 JWT token）
    if ((urlToken && !isJWTToken) || urlCode) {
      console.log('🔍 InviteAccept: URL 中有邀請 token/code，自動載入');
      loadInviteInfo(urlToken && !isJWTToken ? urlToken : null, urlCode);
      return;
    }

    // 情況 4: 沒有 URL 參數，顯示輸入框
    setState('idle');
  }, []);

  /**
   * 載入邀請資訊（公開 API，不需要登入）
   */
  const loadInviteInfo = async (
    token?: string | null,
    code?: string | null
  ) => {
    try {
      setState('loading');
      setError(null);

      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

      // 驗證參數（確保不是空字串）
      const hasValidToken = token && token.trim() !== '';
      const hasValidCode = code && code.trim() !== '';

      if (!hasValidToken && !hasValidCode) {
        setError('請提供邀請連結或邀請碼');
        setState('error');
        return;
      }

      // 構建查詢參數
      const params = new URLSearchParams();
      if (hasValidToken) params.set('token', token.trim());
      if (hasValidCode) params.set('code', code.trim().toUpperCase());

      const response = await fetch(
        `${apiBase}/api/invites/info?${params.toString()}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setInviteInfo(result.data);
          setInviteToken(token || null);
          setInviteCode(code || result.data.invite_code);
          setState('confirm');
        } else {
          setError('無法取得邀請資訊');
          setState('error');
        }
      } else if (response.status === 404) {
        setError('邀請不存在或已過期');
        setState('error');
      } else if (response.status === 400) {
        const errorData = await response.json();
        console.error('[InviteAccept] 400 錯誤:', errorData);
        setError(errorData.message || '邀請連結或邀請碼格式錯誤');
        setState('error');
      } else {
        const errorData = await response.json();
        console.error('[InviteAccept] API 錯誤:', {
          status: response.status,
          error: errorData,
        });
        setError(errorData.message || '無法取得邀請資訊');
        setState('error');
      }
    } catch (error) {
      console.error('載入邀請資訊失敗:', error);
      setError('無法載入邀請資訊');
      setState('error');
    }
  };

  /**
   * 處理手動輸入邀請碼
   */
  const handleManualInput = async () => {
    if (!inviteCode.trim()) {
      setError('請輸入邀請碼');
      return;
    }

    await loadInviteInfo(null, inviteCode.trim().toUpperCase());
  };

  /**
   * 接受邀請（需要登入）
   */
  const handleAcceptInvite = async (
    token?: string | null,
    code?: string | null
  ) => {
    try {
      setState('accepting');

      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const userToken = localStorage.getItem('tenant_token');

      if (!userToken) {
        // 未登入：保存邀請資訊並導向 OAuth
        if (token) {
          sessionStorage.setItem('pending_invite_token', token);
        }
        if (code) {
          sessionStorage.setItem('pending_invite_code', code);
        }

        const redirectUri = `${window.location.origin}/invite/accept`;
        window.location.href = `${apiBase}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
        return;
      }

      // 已登入：直接調用接受 API
      const requestBody: { token?: string; code?: string } = {};
      if (token) requestBody.token = token;
      if (code) requestBody.code = code;

      const response = await fetch(`${apiBase}/api/invites/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          toast.success('成功接受邀請！');
          localStorage.setItem('tenant_id', result.data.tenant_id);
          window.dispatchEvent(new Event('localStorageChange'));

          // 清除 URL 參數
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('token');
          newUrl.searchParams.delete('code');
          window.history.replaceState({}, '', newUrl.toString());

          setState('success');

          // 重定向到 staff-setup 頁面
          setTimeout(() => {
            window.location.href = '/staff-setup';
          }, 1500);
        } else {
          setError('接受邀請失敗');
          setState('error');
        }
      } else if (response.status === 401) {
        // JWT token 過期，需要重新登入
        localStorage.removeItem('tenant_token');
        localStorage.removeItem('tenant_id');

        if (token) {
          sessionStorage.setItem('pending_invite_token', token);
        }
        if (code) {
          sessionStorage.setItem('pending_invite_code', code);
        }

        const redirectUri = `${window.location.origin}/invite/accept`;
        window.location.href = `${apiBase}/api/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
      } else {
        const errorData = await response.json();
        setError(errorData.message || '接受邀請失敗');
        setState('error');
      }
    } catch (error) {
      console.error('接受邀請失敗:', error);
      setError('接受邀請時發生錯誤');
      setState('error');
    }
  };

  /**
   * 取得角色標籤
   */
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
        return '擁有者';
      case 'MANAGER':
        return '管理員';
      case 'STAFF':
        return '工作人員';
      default:
        return role;
    }
  };

  /**
   * 檢查是否已登入
   */
  const isLoggedIn = () => {
    return !!localStorage.getItem('tenant_token');
  };

  // 載入中
  if (state === 'loading' || state === 'accepting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">
            {state === 'loading' ? '載入邀請資訊中...' : '正在接受邀請...'}
          </p>
        </div>
      </div>
    );
  }

  // 成功
  if (state === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-green-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            成功接受邀請！
          </h2>
          <p className="text-gray-600 mb-6">正在跳轉到設定頁面...</p>
        </div>
      </div>
    );
  }

  // 錯誤
  if (state === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            無法接受邀請
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="secondary"
              onClick={() => {
                setState('idle');
                setError(null);
                setInviteInfo(null);
              }}
            >
              重新輸入
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              返回首頁
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 確認頁面（顯示邀請資訊）
  if (state === 'confirm' && inviteInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            {/* 小圖示：有 VITE_INVITE_LOGO_URL 則顯示圖片，否則顯示漸層圓＋文字（改 INVITE_LOGO_LABEL 或 env） */}
            {INVITE_LOGO_URL ? (
              <img
                src={INVITE_LOGO_URL}
                alt=""
                className="w-16 h-16 rounded-full object-cover mx-auto mb-4 shadow-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-white text-base font-bold text-center">
                  {INVITE_LOGO_LABEL}
                </span>
              </div>
            )}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">邀請詳情</h2>
          </div>

          <div className="space-y-4 mb-6">
            {/* 商家名稱 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">商家名稱</div>
              <div className="text-lg font-semibold text-gray-900">
                {inviteInfo.tenant_name}
              </div>
            </div>

            {/* 角色 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">角色</div>
              <div className="text-lg font-semibold text-gray-900">
                {getRoleLabel(inviteInfo.role)}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setState('idle');
                setInviteInfo(null);
                setInviteCode('');
                setInviteToken(null);
              }}
              className="flex-1"
            >
              取消
            </Button>
            {isLoggedIn() ? (
              <Button
                variant="primary"
                onClick={() => handleAcceptInvite(inviteToken, inviteCode)}
                className="flex-1"
              >
                確認加入
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => handleAcceptInvite(inviteToken, inviteCode)}
                className="flex-1"
              >
                登入並接受
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 初始狀態：顯示輸入框
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          {INVITE_LOGO_URL ? (
            <img
              src={INVITE_LOGO_URL}
              alt=""
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
          )}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加入商家</h2>
          <p className="text-gray-600 mb-1">請輸入您的邀請碼</p>
          <p className="text-sm text-gray-500">或使用邀請連結</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              邀請碼
            </label>
            <input
              type="text"
              value={inviteCode}
              onChange={e => setInviteCode(e.target.value.toUpperCase())}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  handleManualInput();
                }
              }}
              placeholder="例如：OPSWCD2ONFWW"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono uppercase tracking-wider"
              autoFocus
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              提示：邀請碼通常為 12 位大寫字母和數字
            </p>
          </div>

          <Button
            variant="primary"
            onClick={handleManualInput}
            className="w-full"
            disabled={!inviteCode.trim()}
          >
            查看邀請詳情
          </Button>
        </div>
      </div>
    </div>
  );
}
